import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';

import { AppUserService } from '../../core/services';
import {
  AssignPackageHistoryItem,
  AssignPackageRecord,
  SubscriptionService
} from '../../core/services/subscription.service';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { EntityListComponent, EntityListMenuAction, TableHeaderAndValueKeySets } from '../../core/ui/entity-list/entity-list.component';
import { AddEditAssignPackageComponent, AssignPackageSaveEvent } from './add-edit-assign-package/add-edit-assign-package.component';
import { TranslatePipe } from '../../core/pipes/translate.pipe';
import {
  buildAssignPackageReceiptData,
  buildAssignPackageReceiptPdfAttachment,
  downloadAssignPackageReceiptPdf,
  printAssignPackageReceiptPdf
} from './assign-package-receipt.util';

interface AssignPackageRow extends AssignPackageRecord {
  userTopLine: string;
  userBottomLine: string;
  packageLabel: string;
  priceLabel: string;
  totalPriceLabel: string;
  durationLabel: string;
  expireDateLabel: string;
  scaleTopLine: string;
  scaleBottomLine: string;
}

@Component({
  selector: 'app-assign-package',
  standalone: true,
  imports: [CommonModule, MatSnackBarModule, EntityListComponent, AddEditAssignPackageComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './assign-package.component.html',
  styleUrls: ['./assign-package.component.css', './../../../CSSFiles/BTN_Style.css']
})
export class AssignPackageComponent implements OnInit {
  isLoading = false;
  isDeleting = false;
  assignments: AssignPackageRow[] = [];
  error: string | null = null;
  filters: Record<string, any> = {};
  selectedAssignment: AssignPackageRecord | null = null;
  selectedHistoryItem: AssignPackageHistoryItem | null = null;
  pendingDeleteAssignment: AssignPackageRecord | null = null;
  selectedAssignmentHistory: AssignPackageHistoryItem[] = [];
  isDetailsOpen = false;
  isEditOpen = false;
  isHistoryOpen = false;
  isHistoryDetailsOpen = false;

  private userLookup = new Map<number, string>();

  readonly assignPackageMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true },
    { key: 'history', label: 'action.history' },
    { key: 'receiptDownload', label: 'action.receiptDownload' },
    { key: 'receiptPrint', label: 'action.receiptPrint' },
    { key: 'sendReceipt', label: 'action.sendReceipt' }
  ];

  readonly tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    'table.name': {
      topLineKey: 'userTopLine',
      bottomLineKyes: ['userBottomLine']
    },
    'package.table.name': 'packageLabel',
    'table.amount': 'priceLabel',
    'assignPackage.table.duration': 'durationLabel',
    'assignPackage.table.scale': {
      topLineKey: 'scaleTopLine',
      bottomLineKyes: ['scaleBottomLine']
    },
    'assignPackage.table.expireDate': 'expireDateLabel'
  };

  constructor(
    private readonly subscriptionService: SubscriptionService,
    private readonly appUserService: AppUserService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.refreshReferenceLookups();
    this.fetchAssignments();
  }

  openCreateModal(): void {
    this.selectedAssignment = null;
    this.isEditOpen = true;
  }

  fetchAssignments(): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.subscriptionService.listAssignPackages({
      pagination: true,
      pageNo: 0,
      rowCount: 100,
      filters: this.normalizedFilters()
    }).pipe(
      finalize(() => (this.isLoading = false))
    ).subscribe({
      next: (res) => {
        if (`${res?.code ?? ''}` !== '000') {
          this.error = `${res?.message ?? 'Failed to fetch assigned packages.'}`;
          return;
        }

        this.assignments = (res.data || []).map((item) => this.toRow(item));
      },
      error: (err: any) => {
        this.error = err?.message || 'Failed to fetch assigned packages.';
      }
    });
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedAssignment = item as AssignPackageRecord;
    this.isDetailsOpen = true;
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.selectedAssignment = item as AssignPackageRecord;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    this.pendingDeleteAssignment = item as AssignPackageRecord;
  }

  onMenuAction(event: { actionKey: string; item: Record<string, any> }): void {
    const selected = event.item as AssignPackageRecord;
    if (event.actionKey === 'receiptDownload') {
      void downloadAssignPackageReceiptPdf(this.buildReceiptData(selected));
      return;
    }
    if (event.actionKey === 'receiptPrint') {
      void printAssignPackageReceiptPdf(this.buildReceiptData(selected));
      return;
    }
    if (event.actionKey === 'sendReceipt') {
      void this.sendReceiptByEmail(selected);
      return;
    }
    if (event.actionKey !== 'history') {
      return;
    }

    const assignPackageId = Number(selected?.id);
    if (!Number.isFinite(assignPackageId)) {
      return;
    }

    this.selectedAssignment = selected;
    this.selectedAssignmentHistory = [];
    this.isHistoryOpen = true;

    this.subscriptionService.listAssignPackageHistory({
      pagination: true,
      pageNo: 0,
      rowCount: 100,
      filters: { assignPackageId }
    }).subscribe({
      next: (res) => {
        if (`${res?.code ?? ''}` !== '000') {
          this.error = `${res?.message ?? 'Failed to load assign package history.'}`;
          return;
        }

        this.selectedAssignmentHistory = res.data || [];
      },
      error: (err: any) => {
        this.error = err?.message || 'Failed to load assign package history.';
      }
    });
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.selectedAssignment = null;
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
    this.selectedAssignment = null;
  }

  onEditSave(_: AssignPackageSaveEvent): void {
    this.closeEditModal();
    this.fetchAssignments();
  }

  closeDeleteModal(): void {
    if (!this.isDeleting) {
      this.pendingDeleteAssignment = null;
    }
  }

  closeHistoryModal(): void {
    this.isHistoryOpen = false;
    this.selectedAssignmentHistory = [];
    this.selectedHistoryItem = null;
    this.isHistoryDetailsOpen = false;
  }

  openHistoryDetails(item: AssignPackageHistoryItem): void {
    this.selectedHistoryItem = item;
    this.isHistoryDetailsOpen = true;
  }

  closeHistoryDetailsModal(): void {
    this.isHistoryDetailsOpen = false;
    this.selectedHistoryItem = null;
  }

  confirmDeleteAssignPackage(): void {
    const target = this.pendingDeleteAssignment;
    const assignPackageId = Number(target?.id);
    if (!Number.isFinite(assignPackageId) || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.subscriptionService.deleteAssignPackage(assignPackageId)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: (res: any) => {
          if (`${res?.code ?? ''}` !== '000') {
            this.error = `${res?.message ?? 'Failed to delete assigned package.'}`;
            this.pendingDeleteAssignment = null;
            return;
          }
          this.pendingDeleteAssignment = null;
          this.fetchAssignments();
        },
        error: (err: any) => {
          this.error = err?.message || 'Failed to delete assigned package.';
          this.pendingDeleteAssignment = null;
        }
      });
  }

  formatPrice(value: number): string {
    const amount = Number(value ?? 0);
    return new Intl.NumberFormat('en-BD', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2
    }).format(amount);
  }

  historyByLabel(history: AssignPackageHistoryItem): string {
    if (history.updatedBy != null || history.updatedByUser) {
      return this.actorLabel(history.updatedByUser, history.updatedBy);
    }
    return this.actorLabel(history.createdByUser, history.createdBy);
  }

  hasRecordedUpdate(item: { createdOn?: string | null; updatedOn?: string | null }): boolean {
    const updatedOn = `${item.updatedOn ?? ''}`.trim();
    if (!updatedOn) {
      return false;
    }
    const createdOn = `${item.createdOn ?? ''}`.trim();
    return !createdOn || updatedOn !== createdOn;
  }

  hasUpdatedActor(
    item: {
      updatedBy?: number | null;
      updatedByUser?: {
        organizationName?: string;
        name?: string;
        username?: string;
        email?: string;
      } | null;
    }
  ): boolean {
    return item.updatedBy != null || !!item.updatedByUser;
  }

  actorLabel(
    user: { organizationName?: string; name?: string; username?: string; email?: string } | null | undefined,
    fallbackId?: number | null
  ): string {
    const label = `${user?.organizationName ?? user?.name ?? user?.username ?? user?.email ?? ''}`.trim();
    if (label) {
      return label;
    }
    return fallbackId != null ? `User #${fallbackId}` : 'N/A';
  }

  totalPrice(value: number, quantity: number | null | undefined): number {
    return Number(value ?? 0) * Number(quantity ?? 0);
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      const day = `${date.getDate()}`.padStart(2, '0');
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    const parts = `${value}`.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return 'N/A';
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;

    return `${day}-${month}-${year} ${`${hours12}`.padStart(2, '0')}:${minutes} ${period}`;
  }

  durationLabel(durationType: number): string {
    if (durationType === 1) {
      return 'Monthly';
    }
    if (durationType === 2) {
      return 'Yearly';
    }
    if (durationType === 3) {
      return 'Custom';
    }
    return 'N/A';
  }

  resolveHistoryUserName(userId: number): string {
    return this.userLookup.get(Number(userId)) || `User #${userId}`;
  }

  formatStorage(value: number): string {
    const memory = Number(value ?? 0);
    if (memory >= 1024) {
      const gb = memory / 1024;
      return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
    }
    return `${memory} MB`;
  }

  remarksLabel(value: string | null | undefined): string {
    const text = `${value ?? ''}`.trim();
    return text || 'N/A';
  }

  historySummary(history: AssignPackageHistoryItem): string {
    const duration = `${this.durationLabel(history.durationUnite)} | ${history.durationQuantity || 0} Qty`;
    const limits = `${history.maxActiveFlat || 0}F | ${history.maxActiveRenter || 0}R | ${history.maxActiveEmployee || 0}SU`;
    const storage = this.formatStorage(history.maxFileStorage);
    const expire = history.expireDate ? this.formatDate(history.expireDate) : 'N/A';
    return `${duration} | ${limits} | ${storage} | Exp ${expire}`;
  }

  private toRow(item: AssignPackageRecord): AssignPackageRow {
    const userTopLine = `${item.appUser?.organizationName ?? item.appUser?.name ?? ''}`.trim() || `User #${item.userId}`;
    const userBottomLine = `${item.appUser?.mobileNumber ?? item.appUser?.email ?? item.appUser?.username ?? ''}`.trim() || 'N/A';
    return {
      ...item,
      userTopLine,
      userBottomLine,
      packageLabel: item.packageName || 'N/A',
      priceLabel: this.formatPrice(item.price),
      totalPriceLabel: this.formatPrice(this.totalPrice(item.price, item.durationQuantity)),
      durationLabel: this.durationLabel(item.durationUnite),
      scaleTopLine: `${item.maxActiveFlat || 0}F | ${item.maxActiveRenter || 0}R | T ${this.formatPrice(this.totalPrice(item.price, item.durationQuantity))}`,
      scaleBottomLine: `${item.maxActiveEmployee || 0} SU | ${this.formatStorage(item.maxFileStorage)} | ${item.durationQuantity || 0}Q${item.isTrial ? ' [[badge-warning:TRIAL]]' : ''}`,
      expireDateLabel: item.expireDate ? this.formatDate(item.expireDate) : 'N/A'
    };
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...filters };
    this.fetchAssignments();
  }

  private refreshReferenceLookups(): void {
    this.appUserService.listUsers({
      pagination: true,
      pageNo: 0,
      rowCount: 200,
      filters: { status: 1, role: 2 }
    }).subscribe({
      next: (res) => {
        this.userLookup = new Map((res.data || []).map((item) => {
          const label = `${item.organizationName ?? item.name ?? ''}`.trim() || `User #${item.id}`;
          return [Number(item.id), label];
        }));
      }
      });
  }

  private normalizedFilters(): Record<string, any> {
    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }
    return filters;
  }

  private buildReceiptData(assignment: AssignPackageRecord) {
    return buildAssignPackageReceiptData(assignment, {
      formatPrice: (value) => this.formatPrice(value),
      formatDate: (value) => this.formatDate(value),
      formatDateTime: (value) => this.formatDateTime(value),
      formatStorage: (value) => this.formatStorage(value),
      durationLabel: (value) => this.durationLabel(value),
      remarksLabel: (value) => this.remarksLabel(value)
    });
  }

  private async sendReceiptByEmail(assignment: AssignPackageRecord): Promise<void> {
    const email = `${assignment.appUser?.email ?? ''}`.trim();
    if (!email) {
      this.error = 'User email is missing.';
      this.openToast(this.error, ['snackbar-error']);
      return;
    }

    try {
      const pdfAttachment = await buildAssignPackageReceiptPdfAttachment(this.buildReceiptData(assignment));
      this.subscriptionService.sendAssignPackageReceiptByEmail({
        assignPackageId: Number(assignment.id),
        email,
        ...pdfAttachment
      }).subscribe({
        next: (res) => {
          const message = `${res?.message ?? 'Receipt sent by email successfully.'}`;
          if (`${res?.code ?? ''}` !== '000') {
            this.error = message;
            this.openToast(message, ['snackbar-error']);
            return;
          }
          this.error = null;
          this.openToast(message, ['snackbar-success']);
        },
        error: (err: any) => {
          const message = err?.message || 'Failed to send receipt by email.';
          this.error = message;
          this.openToast(message, ['snackbar-error']);
        }
      });
    } catch {
      const message = 'Failed to prepare receipt PDF.';
      this.error = message;
      this.openToast(message, ['snackbar-error']);
    }
  }

  private openToast(message: string, panelClass: string[]): void {
    this.snackBar.open(message, 'Close', {
      duration: 3200,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass
    });
  }
}
